import first from "lodash/first";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "~/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "~/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homeAroundMorningStudioPrimaryContentItemContent$key } from "~/queries/__generated__/homeAroundMorningStudioPrimaryContentItemContent.graphql";

import {
  Container,
  Content,
  CoverImage,
  Headline,
  SponsorContainer,
  SponsorName,
  SponsorType,
  StyledEntityLink,
} from "./styles";

export type Props = {
  reference: homeAroundMorningStudioPrimaryContentItemContent$key;
} & ContentItemRenderVariantProps;

const Component = forwardRef<HTMLDivElement, Props>(
  ({ className, onClick, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment homeAroundMorningStudioPrimaryContentItemContent on Content {
          entityId
          urlAlias
          topics {
            sponsor {
              name
              type
            }
          }
          ...entityLink
          ...hooksContentItemProvidersContent
            @arguments(withCoverImage: true, withCoverImageSize540x360: true, withHeadline: true)
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(content);

    const sponsor = first(content?.topics)?.sponsor;
    const sponsorName = sponsor?.name;
    const sponsorType = sponsor?.type;

    return (
      <Container className={className} ref={reference}>
        <StyledEntityLink
          reference={content}
          onClick={() => onClick?.(content.entityId, content.urlAlias)}
        >
          <CoverImage>
            {providers.coverImage({
              responsiveVariants: {
                desktopUp: "size540x360",
                mobileUp: "size540x360",
                tabletUp: "size540x360",
              },
              withCaption: false,
            })}
          </CoverImage>
          <Content>
            <Headline>
              {providers.headline({
                hideTooltip: true,
                preferSocialHeadline: true,
                skipHeadlineSponsorStyle: true,
              })}
            </Headline>
          </Content>
        </StyledEntityLink>
        <SponsorContainer>
          <SponsorType>{sponsorType}</SponsorType>
          <SponsorName>{sponsorName}</SponsorName>
        </SponsorContainer>
      </Container>
    );
  },
);

Component.displayName = "ContentItemHomeAroundMorningStudioPrimary";

export const ContentItemHomeAroundMorningStudioPrimary = withHighlightedHeadline(Component);
